/**
 * @fileoverview Module metadata generation for derivative apps
 * 
 * @purpose Provides functions to generate module metadata from installed modules.
 *          Used by derivative apps to create ModuleRegistration[] for ModuleService.
 * 
 * @usage Import in derivative app: import { generateModuleMetadata } from '@korvo/core/derivative-app/module-metadata'
 * 
 * @ownership Korvo core team
 */

import { type ModuleRegistration } from '../shared/types/types/module-boundaries';

/**
 * Default module metadata - provides basic metadata for known modules
 */
export const defaultModuleMetadata: Record<string, Omit<ModuleRegistration, 'id'>> = {
  chat: {
    name: 'Chat',
    menuItem: {
      id: 'chat',
      label: 'Chat',
      icon: 'ri-chat-1-line',
      color: 'var(--primary-default)',
      href: '/chat',
    },
    notificationTypes: ['message', 'mention'],
  },
  editor: {
    name: 'Editor',
    menuItem: {
      id: 'editor',
      label: 'Editor',
      icon: 'ri-edit-2-line',
      color: 'var(--success-default)',
      href: '/editor',
    },
    notificationTypes: ['comment', 'share', 'edit'],
  },
  mentor: {
    name: 'Mentor',
    menuItem: {
      id: 'mentor',
      label: 'Mentor',
      icon: 'ri-target-line',
      color: '#7c3aed',
      href: '/mentor',
    },
    notificationTypes: ['session_complete', 'mentor_message'],
  },
  rag: {
    name: 'RAG',
    menuItem: {
      id: 'rag',
      label: 'RAG',
      icon: 'ri-database-2-line',
      color: 'var(--danger-default)',
      href: '/rag',
    },
  },
  workspace: {
    name: 'Workspace',
    menuItem: {
      id: 'workspace',
      label: 'Workspace',
      icon: 'ri-folder-open-line',
      color: '#0d9488',
      href: '/editor',
    },
    notificationTypes: [],
  },
};

/**
 * Generate module metadata from available module IDs
 * 
 * @param availableModuleIds - Array of module IDs from generated-module-registry
 * @param customMetadata - Optional custom metadata to override or extend defaults
 * @returns Array of ModuleRegistration objects
 */
export function generateModuleMetadata(
  availableModuleIds: string[],
  customMetadata?: Record<string, Partial<Omit<ModuleRegistration, 'id'>>>,
  descriptorPartials?: Record<string, Partial<Omit<ModuleRegistration, 'id'>>>
): ModuleRegistration[] {
  const merged: Record<string, Partial<Omit<ModuleRegistration, 'id'>>> = {}
  for (const key of Object.keys(customMetadata ?? {})) {
    merged[key] = { ...customMetadata![key], ...descriptorPartials?.[key] }
  }
  for (const key of Object.keys(descriptorPartials ?? {})) {
    if (!(key in merged)) {
      merged[key] = descriptorPartials![key]
    }
  }

  return availableModuleIds.map(moduleId => {
    const defaultMeta = defaultModuleMetadata[moduleId];
    const customMeta = merged[moduleId];
    
    if (!defaultMeta && !customMeta) {
      // Fallback for unknown modules - provide basic metadata
      return {
        id: moduleId,
        name: moduleId.charAt(0).toUpperCase() + moduleId.slice(1),
        menuItem: {
          id: moduleId,
          label: moduleId.charAt(0).toUpperCase() + moduleId.slice(1),
          icon: 'ri-apps-line',
          color: 'var(--foreground-subtle)',
          href: `/${moduleId}`,
        },
      };
    }
    
    // Merge default and custom metadata
    return {
      id: moduleId,
      ...defaultMeta,
      ...customMeta,
      menuItem: {
        ...defaultMeta?.menuItem,
        ...customMeta?.menuItem,
      },
    } as ModuleRegistration;
  });
}

